import type { TraceKind } from '@features/dataViewer/traceServices.js';
import {
  type Document,
  DocumentSchema,
} from '@features/documents/model/actions.js';
import type { BatchKind } from '@shared/batchProperties.js';
import z from 'zod';
import { DOCUMENTS_IN_MANUAL } from '@/manual/moved.js';

const URL_MODAL_IDS = [
  'about',
  'account',
  'browse-cache',
  'credits-purchase',
  'drawing-properties',
  'elevation-settings',
  'embed',
  'file-import',
  'events-filter',
  'gallery-filter',
  'gallery-leaderboard',
  'gallery-upload',
  'gps-recorder-settings',
  'legend',
  'login',
  'manual',
  'map-features-export',
  'map-manager',
  'map-preferences',
  'map-to-document-export',
  'my-maps',
  'objects-style',
  'offline-map-export',
  'offline-maps',
  'panorama-settings',
  'premium',
  'route-planner-style',
  'search-result-style',
  'support-us',
  'toposcope-settings',
  'track-viewer-match',
  'track-viewer-osm-upload',
  'tracking-my',
  'tracking-watched',
  'viewshed-settings',
] as const;

export const UrlModalIdSchema = z.enum(URL_MODAL_IDS);

/** Legacy modal ids (from old shared links) mapped to their current name. */
const MODAL_RENAMES: Record<string, string> = {
  'track-viewer-style': 'drawing-properties',
  'export-map': 'map-to-document-export',
  'export-gpx': 'map-features-export',
  'export-map-features': 'map-features-export',
  'export-pdf': 'map-to-document-export',
  'download-map': 'offline-map-export',
  supportUs: 'support-us',
  mapSettings: 'map-manager',
  'map-settings': 'map-manager',
  'map-layers-config': 'map-manager',
  'custom-maps': 'map-manager',
  'map-library': 'map-manager',
  'installed-maps': 'map-manager',
  'available-maps': 'map-manager',
  'remove-ads': 'premium',
  'upload-track': 'file-import',
  'buy-credits': 'credits-purchase',
  maps: 'my-maps',
  'premium-switch': 'premium',
};

/**
 * Modals an embedded map refuses to open: those that manage the visitor's own
 * maps, or that sign them in and take their money — neither of which belongs
 * in an iframe on somebody else's page. Their buttons are hidden there and the
 * keyboard chords never start, so a `show=` in the iframe's own URL is the last
 * way in; premium reaches its modal by opening the portal in a tab of its own.
 */
const EMBED_FORBIDDEN_MODAL_IDS = new Set<string>([
  'browse-cache',
  'elevation-settings',
  'event-form',
  'map-manager',
  'map-preferences',
  'offline-maps',
  'premium',
  'track-viewer-osm-upload',
]);

// One that names no id of its own: it edits whatever is selected, which a
// `show=` in a shared link knows nothing about.
export const ModalIdSchema = z.enum([
  ...URL_MODAL_IDS,
  'data-viewer-properties',
  'data-viewer-vertex',
  'event-form',
]);

export type ModalId = z.infer<typeof ModalIdSchema>;

/** The account modal's parts, as `show=account/<section>` names them. */
export const AccountSectionSchema = z.enum([
  'payments',
  'personal',
  'providers',
  'app-tokens',
  'roles',
]);

export type AccountSection = z.infer<typeof AccountSectionSchema>;

// Modals whose `show=` argument only says which of their sections is open.
const SECTION_MODALS = new Set<string>(['account']);

export function isSectionModal(type: string): boolean {
  return SECTION_MODALS.has(type);
}

/** A packed `show=` value without the section of a modal that has them. */
export function showWithoutSection(show: string): string {
  const type = show.split('/')[0];

  return isSectionModal(type) ? type : show;
}

/** Which form the library shows: a custom map's or a preset's; a new one's without `edit`. */
export type CustomMapRequest = {
  /** The custom map or preset to edit. */
  edit?: string;
  /** A new preset of what is on the map, asked for by the Map layers panel. */
  addPreset?: boolean;
  /** A copy of this preset (a link's, or a duplicate) as the account's own, in its place on the map. */
  addPresetFrom?: string;
  /** A named map of this drawing of a map, in its place; see `mapNamedMapCreate`. */
  addNamedFrom?: { type: string; preset?: string };
  /** Cancel closes the modal rather than going back to the list. */
  closeOnCancel?: boolean;
};

/**
 * The open modal/overlay. A discriminated union so a modal can carry an
 * argument (e.g. the document key or watched-device token). At most one is open
 * at a time. `null` means no modal.
 */
export type ActiveModal =
  | {
      type: Exclude<
        ModalId,
        | 'tracking-watched'
        | 'my-maps'
        | 'map-manager'
        | 'manual'
        | 'event-form'
        | 'account'
      >;
    }
  /** `section` is the part opened; without it, all are closed. */
  | { type: 'account'; section?: AccountSection }
  /** Without `chapter`, the first one. */
  | { type: 'manual'; chapter?: string }
  | { type: 'my-maps'; add?: boolean }
  /** Edits the event `id`; without it a new one, linking `mapId` if given. */
  | { type: 'event-form'; id?: string; mapId?: string }
  /**
   * The map manager. With `customMap` it shows the form for one; see
   * `CustomMapRequest`. `highlight` picks out that map in the list; `query`
   * fills in its search.
   */
  | {
      type: 'map-manager';
      customMap?: CustomMapRequest;
      highlight?: string;
      query?: string;
    }
  | { type: 'tracking-watched'; token?: string }
  /** The properties of every feature of a kind at once. */
  | { type: 'data-viewer-batch-properties'; kind: BatchKind }
  /** What to trace within the selected polygon. */
  | { type: 'data-viewer-trace'; kind: TraceKind; legacy?: boolean }
  | { type: 'document'; key: Document }
  | { type: 'gallery-viewer'; id: number }
  /** `returnTo` is what Close goes back to; it is not in the URL. */
  | { type: 'user'; id: number; returnTo?: ActiveModal }
  | { type: 'wiki'; key: string };

/**
 * Serializes the open modal to the packed `show=` value (`type` or `type/arg`),
 * or `null` when it has no URL representation. The literal `/` survives because
 * `serializeQuery` un-escapes `%2F`.
 */
export function encodeActiveModal(modal: ActiveModal | null): string | null {
  if (!modal) {
    return null;
  }

  switch (modal.type) {
    case 'tracking-watched':
      return modal.token
        ? `tracking-watched/${modal.token}`
        : 'tracking-watched';
    case 'document':
      return `document/${modal.key}`;
    case 'manual':
      return modal.chapter ? `manual/${modal.chapter}` : 'manual';
    case 'account':
      return modal.section ? `account/${modal.section}` : 'account';
    case 'event-form':
      return modal.id
        ? `event-form/${modal.id}`
        : modal.mapId
          ? `event-form/from/${modal.mapId}`
          : 'event-form';
    case 'gallery-viewer':
      return `gallery-viewer/${modal.id}`;
    case 'user':
      return `user/${modal.id}`;
    case 'wiki':
      return `wiki/${modal.key}`;
    default:
      // Only URL-serializable ids go in `show=`; data-viewer-properties doesn't.
      return UrlModalIdSchema.safeParse(modal.type).success ? modal.type : null;
  }
}

/**
 * Parses a packed `show=` value (`type` or `type/arg`) into an `ActiveModal`,
 * applying legacy name renames. Returns `null` when nothing valid is named.
 */
export function decodeActiveModal(raw: string): ActiveModal | null {
  if (!raw) {
    return null;
  }

  const slash = raw.indexOf('/');

  const rawType = slash === -1 ? raw : raw.slice(0, slash);

  const arg = slash === -1 ? undefined : raw.slice(slash + 1);

  const type = MODAL_RENAMES[rawType] ?? rawType;

  // After the rename, so an old name for a forbidden modal is refused too.
  if (window.fmEmbedded && EMBED_FORBIDDEN_MODAL_IDS.has(type)) {
    return null;
  }

  switch (type) {
    case 'tracking-watched':
      return arg
        ? { type: 'tracking-watched', token: arg }
        : { type: 'tracking-watched' };
    case 'document': {
      const chapter = arg === undefined ? undefined : DOCUMENTS_IN_MANUAL[arg];

      if (chapter !== undefined) {
        return { type: 'manual', chapter };
      }

      const r = DocumentSchema.safeParse(arg);

      return r.success ? { type: 'document', key: r.data } : null;
    }
    case 'manual':
      return arg ? { type: 'manual', chapter: arg } : { type: 'manual' };
    case 'account': {
      // An unknown section still opens the modal.
      const section = AccountSectionSchema.safeParse(arg);

      return section.success
        ? { type: 'account', section: section.data }
        : { type: 'account' };
    }
    case 'event-form':
      return !arg
        ? { type: 'event-form' }
        : arg.startsWith('from/')
          ? { type: 'event-form', mapId: arg.slice(5) }
          : { type: 'event-form', id: arg };
    case 'gallery-viewer': {
      const id = Number(arg);

      return arg && Number.isFinite(id) ? { type: 'gallery-viewer', id } : null;
    }
    case 'user': {
      const id = Number(arg);

      return arg && Number.isInteger(id) && id > 0
        ? { type: 'user', id }
        : null;
    }
    case 'wmc': {
      // Legacy Wikimedia Commons deep links now open in the merged gallery
      // viewer, where Commons photos are addressed as negative ids (`-pageId`).
      const pageId = Number(arg);

      return arg && Number.isFinite(pageId)
        ? { type: 'gallery-viewer', id: -pageId }
        : null;
    }
    case 'wiki':
      return arg ? { type: 'wiki', key: arg } : null;
    default: {
      const r = UrlModalIdSchema.safeParse(type);

      return r.success ? modalOf(r.data) : null;
    }
  }
}

/**
 * Wraps an arg-less modal named by a `ModalId` into an `ActiveModal`, for the
 * few call sites that open a modal chosen at runtime.
 *
 * Each branch returns a literal that TypeScript checks against `ActiveModal`,
 * so the value is always a valid member (no cast). The first branch narrows off
 * the ids whose members carry an optional field, leaving the default's
 * `modalId` matching the catch-all member exactly. A `ModalId` that gained a
 * *required* field would then fail to type-check here, forcing it to be handled
 * rather than silently producing an incomplete modal.
 */
export function modalOf(modalId: ModalId): ActiveModal {
  switch (modalId) {
    case 'my-maps':
    case 'map-manager':
    case 'event-form':
    case 'tracking-watched':
    case 'manual':
    case 'account':
      return { type: modalId };
    default:
      return { type: modalId };
  }
}
